import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Clock, Save, Plus, Trash2, AlertTriangle, TrendingUp } from 'lucide-react';

const DAYS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];

function getDaysInMonth(month, year) { return new Date(year, month, 0).getDate(); }
function getFirstDayOfWeek(month, year) { const d = new Date(year, month - 1, 1).getDay(); return d === 0 ? 6 : d - 1; }
function formatDate(d) { return d.toISOString().split('T')[0]; }
function displayDate(dateStr) { const d = new Date(dateStr + 'T12:00:00'); return d.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' }); }

export default function Asistencia() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [inasistencias, setInasistencias] = useState([]);
  const [excedentes, setExcedentes] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const mes = currentDate.getMonth() + 1;
  const año = currentDate.getFullYear();
  const daysInMonth = getDaysInMonth(mes, año);
  const firstDay = getFirstDayOfWeek(mes, año);

  const fetchVendors = useCallback(async () => {
    try { const data = await api.getVendors(); setVendors(data.filter(v => v.status === 'active')); } catch (e) { /* handled */ }
  }, []);

  const fetchAllRecords = useCallback(async () => {
    try { const data = await api.getAllAsistencia(mes, año); setAllRecords(data); } catch (e) { /* handled */ } finally { setLoading(false); }
  }, [mes, año]);

  const fetchVendorHoras = useCallback(async (vendorId) => {
    try {
      const data = await api.getAsistencia(vendorId, mes, año);
      const inas = [], exc = [];
      (data.registros || []).forEach(r => {
        if (r.horas_inasistencia > 0) inas.push({ fecha: r.fecha, horas: r.horas_inasistencia });
        if (r.horas_excedentes > 0) exc.push({ fecha: r.fecha, horas: r.horas_excedentes });
      });
      setInasistencias(inas);
      setExcedentes(exc);
    } catch (e) { setInasistencias([]); setExcedentes([]); }
  }, [mes, año]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => { fetchAllRecords(); }, [fetchAllRecords]);
  useEffect(() => {
    if (selectedVendor) fetchVendorHoras(selectedVendor.id);
    else { setInasistencias([]); setExcedentes([]); }
  }, [selectedVendor, fetchVendorHoras]);

  const addInasistencia = (dateStr) => {
    if (inasistencias.find(i => i.fecha === dateStr)) return;
    setInasistencias([...inasistencias, { fecha: dateStr, horas: 1 }]);
  };

  const addExcedente = (dateStr) => {
    if (excedentes.find(e => e.fecha === dateStr)) return;
    setExcedentes([...excedentes, { fecha: dateStr, horas: 1 }]);
  };

  const updateInasistencia = (idx, horas) => {
    const updated = [...inasistencias];
    updated[idx] = { ...updated[idx], horas: parseFloat(horas) || 0 };
    setInasistencias(updated);
  };

  const updateExcedente = (idx, horas) => {
    const updated = [...excedentes];
    updated[idx] = { ...updated[idx], horas: parseFloat(horas) || 0 };
    setExcedentes(updated);
  };

  const removeInasistencia = (idx) => setInasistencias(inasistencias.filter((_, i) => i !== idx));
  const removeExcedente = (idx) => setExcedentes(excedentes.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!selectedVendor) return;
    setSaving(true);
    try {
      const dateMap = {};
      inasistencias.forEach(i => { dateMap[i.fecha] = { ...(dateMap[i.fecha] || { horas_inasistencia: 0, horas_excedentes: 0 }), horas_inasistencia: i.horas }; });
      excedentes.forEach(e => { dateMap[e.fecha] = { ...(dateMap[e.fecha] || { horas_inasistencia: 0, horas_excedentes: 0 }), horas_excedentes: e.horas }; });
      const registros = Object.entries(dateMap).map(([fecha, h]) => ({ fecha, ...h }));
      await api.updateAsistencia({ vendedor_id: selectedVendor.id, registros, mes, año });
      toast.success('Horas laborales guardadas');
      fetchAllRecords();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDayClick = (day) => {
    const dateStr = `${año}-${String(mes).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDay(selectedDay === dateStr ? null : dateStr);
  };

  const prevMonth = () => { setCurrentDate(new Date(año, mes - 2, 1)); setInasistencias([]); setExcedentes([]); setSelectedDay(null); };
  const nextMonth = () => { setCurrentDate(new Date(año, mes, 1)); setInasistencias([]); setExcedentes([]); setSelectedDay(null); };
  const monthLabel = currentDate.toLocaleDateString('es-UY', { month: 'long', year: 'numeric' });

  const getVendorTotals = (vendorId) => {
    const record = allRecords.find(r => r.vendedor_id === vendorId);
    return { inasistencia: record?.total_inasistencia || 0, excedentes: record?.total_excedentes || 0 };
  };

  const totalInas = inasistencias.reduce((s, i) => s + i.horas, 0);
  const totalExc = excedentes.reduce((s, e) => s + e.horas, 0);

  const today = new Date();
  const isToday = (day) => day === today.getDate() && mes === today.getMonth() + 1 && año === today.getFullYear();

  const getDayStatus = (day) => {
    const dateStr = `${año}-${String(mes).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const hasInas = inasistencias.some(i => i.fecha === dateStr);
    const hasExc = excedentes.some(e => e.fecha === dateStr);
    return { dateStr, hasInas, hasExc };
  };

  const calendarCells = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  return (
    <div className="space-y-6" data-testid="asistencia">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Horas Laborales</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Control de horas de inasistencia y excedentes</p>
        </div>
        {selectedVendor && (
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-hours">
            <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
          </button>
        )}
      </div>

      {/* Vendor selector + Month */}
      <div className="flex items-center gap-4 flex-wrap">
        <select value={selectedVendor?.id || ''} onChange={(e) => {
          const v = vendors.find(v => v.id === e.target.value);
          setSelectedVendor(v || null); setSelectedDay(null);
        }} className="form-input max-w-xs" data-testid="vendor-select">
          <option value="">Seleccionar vendedor...</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.nombre} - {v.ficha_casmu}</option>)}
        </select>
        <div className="flex items-center gap-3">
          <button onClick={prevMonth} className="btn-ghost p-2" data-testid="prev-month"><ChevronLeft size={18} /></button>
          <span className="text-[15px] font-semibold text-[#0A0A0A] capitalize min-w-[140px] text-center">{monthLabel}</span>
          <button onClick={nextMonth} className="btn-ghost p-2" data-testid="next-month"><ChevronRight size={18} /></button>
        </div>
      </div>

      {selectedVendor ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Module 1: Calendar */}
          <div className="mac-card p-4">
            <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Calendario</h3>
            <div className="grid grid-cols-7 gap-1">
              {DAYS.map(d => <div key={d} className="text-center text-[9px] font-semibold text-gray-400 uppercase py-1">{d}</div>)}
              {calendarCells.map((day, idx) => {
                if (day === null) return <div key={`e-${idx}`} />;
                const { dateStr, hasInas, hasExc } = getDayStatus(day);
                const selected = selectedDay === dateStr;
                return (
                  <button key={dateStr} onClick={() => handleDayClick(day)}
                    className={`relative rounded-lg p-1 h-10 text-[12px] font-medium transition-all border
                      ${selected ? 'bg-[#0082D2] text-white border-[#0082D2]' : ''}
                      ${!selected && isToday(day) ? 'border-[#0082D2] text-[#0082D2]' : ''}
                      ${!selected && !isToday(day) ? 'border-transparent hover:bg-gray-50 text-gray-700' : ''}
                    `}
                    data-testid={`day-${day}`}>
                    {day}
                    {(hasInas || hasExc) && (
                      <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                        {hasInas && <span className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-white/70' : 'bg-[#F59E0B]'}`}></span>}
                        {hasExc && <span className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-white/70' : 'bg-[#10B981]'}`}></span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedDay && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
                <button onClick={() => { addInasistencia(selectedDay); setSelectedDay(null); }}
                  className="flex-1 text-[11px] font-medium py-2 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20 transition-colors"
                  data-testid="add-inas-btn">
                  + Inasistencia
                </button>
                <button onClick={() => { addExcedente(selectedDay); setSelectedDay(null); }}
                  className="flex-1 text-[11px] font-medium py-2 rounded-lg bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 transition-colors"
                  data-testid="add-exc-btn">
                  + Excedente
                </button>
              </div>
            )}
            <div className="mt-3 flex items-center gap-4 text-[10px] text-gray-500">
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59E0B]"></span> Inasistencia</div>
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]"></span> Excedente</div>
            </div>
          </div>

          {/* Module 2: Horas Inasistencia */}
          <div className="mac-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} className="text-[#F59E0B]" />
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Horas Inasistencia</h3>
              </div>
              <span className="text-lg font-bold text-[#F59E0B]">{totalInas}h</span>
            </div>
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {inasistencias.length === 0 ? (
                <p className="text-center text-gray-400 text-[12px] py-6">Sin inasistencias registradas</p>
              ) : (
                inasistencias.sort((a, b) => a.fecha.localeCompare(b.fecha)).map((item, idx) => (
                  <div key={`inas-${item.fecha}`} className="flex items-center gap-2 p-2 rounded-lg bg-[#F59E0B]/[0.04] border border-[#F59E0B]/20">
                    <div className="flex-1">
                      <div className="text-[12px] font-medium text-gray-800">{displayDate(item.fecha)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <input type="number" min="0.5" max="24" step="0.5" value={item.horas}
                        onChange={(e) => updateInasistencia(idx, e.target.value)}
                        className="w-16 text-[12px] px-2 py-1 rounded border border-[#F59E0B]/30 text-center focus:border-[#F59E0B] focus:outline-none"
                        data-testid={`inas-hours-${idx}`} />
                      <span className="text-[10px] text-gray-500">hs</span>
                    </div>
                    <button onClick={() => removeInasistencia(idx)} className="text-gray-400 hover:text-red-500 transition-colors" data-testid={`remove-inas-${idx}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Module 3: Horas Excedentes */}
          <div className="mac-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp size={14} className="text-[#10B981]" />
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Horas Excedentes</h3>
              </div>
              <span className="text-lg font-bold text-[#10B981]">{totalExc}h</span>
            </div>
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {excedentes.length === 0 ? (
                <p className="text-center text-gray-400 text-[12px] py-6">Sin horas excedentes registradas</p>
              ) : (
                excedentes.sort((a, b) => a.fecha.localeCompare(b.fecha)).map((item, idx) => (
                  <div key={`exc-${item.fecha}`} className="flex items-center gap-2 p-2 rounded-lg bg-[#10B981]/[0.04] border border-[#10B981]/20">
                    <div className="flex-1">
                      <div className="text-[12px] font-medium text-gray-800">{displayDate(item.fecha)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <input type="number" min="0.5" max="24" step="0.5" value={item.horas}
                        onChange={(e) => updateExcedente(idx, e.target.value)}
                        className="w-16 text-[12px] px-2 py-1 rounded border border-[#10B981]/30 text-center focus:border-[#10B981] focus:outline-none"
                        data-testid={`exc-hours-${idx}`} />
                      <span className="text-[10px] text-gray-500">hs</span>
                    </div>
                    <button onClick={() => removeExcedente(idx)} className="text-gray-400 hover:text-red-500 transition-colors" data-testid={`remove-exc-${idx}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="mac-card p-12 text-center">
          <Clock size={48} className="mx-auto text-gray-200 mb-4" />
          <p className="text-gray-500 text-[13px]">Selecciona un vendedor para registrar sus horas</p>
        </div>
      )}
    </div>
  );
}
